<template>
  <walkthrough v-if="!isSawWalkthrough && isNative" @finished="onFinishedWalkthrough"/>
  <div dir="rtl" class="h-[100dvh] overflow-y-auto bg-slate-50 flex relative overflow-x-hidden font-sans text-slate-900 selection:bg-pink-200">

    <!-- Background Ambient Glows -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-96 h-96 bg-pink-200/40 rounded-full blur-3xl opacity-70"></div>
      <div class="absolute top-40 -left-20 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl opacity-70"></div>
    </div>

    <!-- Main Card Container -->
    <div class="relative z-10 m-auto w-full md:max-w-5xl md:h-[750px] flex flex-col md:flex-row bg-white md:rounded-[2.5rem] md:shadow-2xl md:border border-slate-100 overflow-hidden min-h-screen md:min-h-0">

      <!-- Right Side: Login Form -->
      <div class="w-full md:w-1/2 flex flex-col px-6 py-10 sm:px-12 sm:py-12 bg-white relative">

        <!-- Native Back Button -->
        <div class="absolute top-6 right-6">
          <floating-button
            v-if="!showFloatingMan && isNative"
            icon="pi-angle-left"
            :insidePage="false"
            @clicked-floating-button="clickBack"
          />
        </div>

        <div class="flex flex-col w-full flex-grow max-w-sm mx-auto justify-center">

          <!-- Logo Header -->
          <div v-if="!isNative" class="flex items-center gap-2 mb-10">
            <div
              class="flex-shrink-0 flex items-center gap-2 cursor-pointer transition-transform hover:scale-105">
              <img src="../../../assets/img/login/logo-sign.svg" alt="" class="w-6 h-6">
              <span class="font-black text-2xl tracking-tight text-slate-800">time<span class="text-primary-600">it</span></span>
            </div>
          </div>

          <!-- Welcome Texts -->
          <h1 class="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2 leading-tight">ברוכים השבים!</h1>
          <p class="text-slate-500 mb-10">העסק שלכם חיכה לכם. בואו נמשיך מאיפה שעצרנו.</p>

          <!-- Form Fields -->
          <div class="space-y-5">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-slate-700" for="username">כתובת אימייל</label>
              <IconField>
                <input-text
                  id="username"
                  v-model="email"
                  type="text"
                  placeholder="mail@example.com"
                  class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-left"
                  dir="ltr"
                />
                <InputIcon class="pi pi-at !text-slate-400" />
              </IconField>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-slate-700" for="password">סיסמה</label>
              <IconField>
                <input-text
                  id="password"
                  v-model="password"
                  type="password"
                  placeholder="הקלידי סיסמה..."
                  class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-left"
                  dir="ltr"
                />
                <InputIcon class="pi pi-lock !text-slate-400" />
              </IconField>
            </div>
          </div>

          <!-- Forgot Password -->
          <div class="flex justify-end mt-2 mb-8">
            <router-link
              class="text-sm font-bold text-pink-600 hover:text-pink-700 transition-colors hover:underline"
              :to="{ name: AuthRouteNames.ResetPassword }">
              שכחת סיסמה?
            </router-link>
          </div>

          <!-- Submit Button -->
          <Button
            v-wave
            class="w-full !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex items-center justify-center"
            :loading="loading"
            @click="signIn"
          >
            <span v-if="!loading" class="text-lg">התחברות למערכת</span>
          </Button>

          <!-- Divider -->
          <Divider
            type="solid"
            align="center"
            class="!my-8"
            layout="horizontal">
            <span class="text-slate-400 text-sm bg-white px-4">או התחברו באמצעות</span>
          </Divider>

          <!-- Social Login -->
          <social-login-buttons />

        </div>

        <!-- Mobile Register Link -->
        <div v-if="showRegister && !showFloatingMan" class="w-full flex justify-center items-center mt-10">
          <div class="px-5 py-3 flex gap-2 rounded-xl bg-slate-50 border border-slate-100 text-sm shadow-sm">
            <span class="text-slate-600">עדיין אין לכם חשבון?</span>
            <router-link class="text-pink-600 font-bold hover:underline" :to="{ name: AuthRouteNames.Register }">
              להרשמה בחינם
            </router-link>
          </div>
        </div>
      </div>

      <!-- Left Side: Premium Visuals (Desktop Only) -->
      <div v-if="showFloatingMan" class="hidden md:flex w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900 p-12 flex-col justify-between relative overflow-hidden text-white">

        <!-- Abstract Decorations -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl"></div>
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>

        <!-- Text Content -->
        <div class="z-10 relative mt-8">
          <h2 class="text-4xl lg:text-5xl font-black leading-tight mb-4 tracking-tight">
            היומן שלכם עובד,<br/>גם כשאתם נחים.
          </h2>
          <p class="text-slate-300 text-lg leading-relaxed max-w-sm">
            תורים אוטומטיים, מניעת הברזות, ושליטה מלאה על ההכנסות. הכל במקום אחד.
          </p>
        </div>

        <!-- Brand New Visual Representation -->
        <div class="z-10 relative flex-1 flex flex-col items-center justify-center mt-10 space-y-5">
          <!-- Notification Card 1 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-green-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-green-400/20 p-3 rounded-xl text-green-300">
                <CheckCircle2 class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">אושר תור חדש ביומן!</p>
                <p class="text-slate-300 text-xs mt-0.5">מחר ב-10:00 • נועה לוי</p>
              </div>
            </div>
          </div>

          <!-- Notification Card 2 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform -translate-x-6 transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-pink-500"></div>
            <div class="flex items-center gap-4">
              <div class="bg-pink-500/20 p-3 rounded-xl text-pink-300">
                <MessageCircle class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">12 תזכורות נשלחו אוטומטית</p>
                <p class="text-slate-300 text-xs mt-0.5">ללקוחות המתוכננות להיום</p>
              </div>
            </div>
          </div>

          <!-- Notification Card 3 -->
          <div class="bg-white/15 border border-white/10 rounded-2xl p-4 w-full max-w-sm transform translate-x-4 transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-blue-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-blue-400/20 p-3 rounded-xl text-blue-300">
                <CreditCard class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">התקבל תשלום באשראי</p>
                <p class="text-slate-300 text-xs mt-0.5">₪150.00 נגבו מראש בהצלחה</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Register Link -->
        <div class="z-10 relative mt-12">
          <div class="bg-white/10 rounded-2xl p-5 border border-white/10 flex justify-between items-center">
            <div>
              <p class="font-bold text-white mb-0.5">עדיין לא איתנו?</p>
              <p class="text-sm text-slate-400">הצטרפו עכשיו ותתחילו להינות.</p>
            </div>
            <router-link
              class="bg-white text-slate-900 hover:bg-slate-100 font-bold py-3 px-6 rounded-xl transition-colors shadow-lg text-sm"
              :to="{ name: AuthRouteNames.Register }">
              הרשמה בחינם
            </router-link>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import Session from 'supertokens-web-js/recipe/session';
import { useToast } from 'vue-toastification';
import { AdminRouteNames, AuthRouteNames } from '@app/router/route-names';
import { useNavigation } from '@app/composables/navigation';
import { useResponsive } from '@app/composables/responsive';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import SocialLoginButtons from '@app/components/auth/SocialLoginButtons.vue';
import { Capacitor } from '@capacitor/core';
import { Sparkles, MessageCircle, CreditCard, CheckCircle2 } from 'lucide-vue-next';
import { useStorage } from '@app/composables/use-storage';
import { trackEvent } from '@app/composables/posthog';

const { isMobile, screenWidth } = useResponsive();
const toast = useToast();
const { goTo } = useNavigation();
const {getValue, setValue} = useStorage();

const loading = ref(false);
const email = ref('');
const password = ref('');
const isSawWalkthrough = ref(true);

const isNative = computed(() => Capacitor.isNativePlatform());
const showRegister = computed(() => {
  return isMobile.value || isNative;
});

const onFinishedWalkthrough = () => {
  setValue('saw-walkthrough', 'true');
  isSawWalkthrough.value = true;
}

const signIn = async (_: Event) => {
  trackEvent('login_clicked');
  loading.value = true;
  try {
    const response = await EmailPassword.signIn({
      formFields: [
        { id: 'email', value: email.value },
        { id: 'password', value: password.value }
      ]
    });
    if (
      response.status === 'WRONG_CREDENTIALS_ERROR' ||
      response.status === 'FIELD_ERROR'
    ) {
      trackEvent('login_failed', { reason: response.status });
      toast.error('הנתונים שהוזנו שגויים');
      loading.value = false;
      return;
    }
    trackEvent('login_succeeded');
    loading.value = false;
    goTo(AdminRouteNames.Home);
  } catch (e) {
    trackEvent('login_failed', { reason: 'exception' });
    loading.value = false;
    console.error(e);
    toast.error('לצערנו ההתחברות נכשלה, אנא נסו שוב');
  }
};

const checkForSession = async () => {
  if (await Session.doesSessionExist()) {
    goTo(AdminRouteNames.Home);
  }
};

const showFloatingMan = computed(() => {
  return screenWidth.value > 768;
});

const clickBack = () => {
  goTo(AuthRouteNames.CustomerLogin);
};

onBeforeMount(() => {
  checkForSession();
  getValue('saw-walkthrough').then(sawWalkthrough => {
    isSawWalkthrough.value = sawWalkthrough === 'true'
  })
});

</script>
